/**
 * PublicFormSigning — public page (no login required) where a client
 * opens a signing link, views the PDF, fills client-relevant fields,
 * captures their signature, and submits.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import SignatureCapture from './components/SignatureCapture';
import logo from './midflorida-logo.svg';

// PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function PublicFormSigning() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorStatus, setErrorStatus] = useState('');  // 'expired', 'completed'
  const [templateName, setTemplateName] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [fieldDefinitions, setFieldDefinitions] = useState({ pages: [] });
  const [formData, setFormData] = useState({});
  const [signatures, setSignatures] = useState({});
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // PDF state
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [signatureModalField, setSignatureModalField] = useState(null);

  const pageContainerRef = useRef(null);

  // Load form data from the public API
  useEffect(() => {
    const fetchForm = async () => {
      try {
        const resp = await fetch(`${BASE_URL}/api/signing/form/${token}/`);
        if (resp.status === 410) {
          const data = await resp.json();
          setErrorStatus(data.status || 'expired');
          setError(data.error || 'This link is no longer valid.');
          setLoading(false);
          return;
        }
        if (!resp.ok) {
          setError('Invalid or expired signing link.');
          setLoading(false);
          return;
        }
        const data = await resp.json();
        setTemplateName(data.template_name);
        setPdfUrl(data.pdf_url);
        setFieldDefinitions(data.field_definitions || { pages: [] });
        setFormData(data.form_data || {});
        setClientName(data.client_name || '');
      } catch (e) {
        setError('Unable to load the form. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
  }, [token]);

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const getCurrentPageFields = useCallback(() => {
    const page = (fieldDefinitions.pages || []).find(p => p.pageNumber === currentPage);
    return page?.fields || [];
  }, [fieldDefinitions, currentPage]);

  // Determine if a field is agent-filled (read-only for client)
  const isAgentField = (field) => {
    const mapping = field.dataMapping || '';
    if (mapping.startsWith('agent.')) return true;
    // Signature fields with agent role are read-only
    if (field.type === 'signature' && field.signatureRole === 'agent') return true;
    return false;
  };

  // Field change handler
  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  // Signature capture
  const handleSignatureCapture = (sigData) => {
    if (signatureModalField) {
      setSignatures(prev => ({
        ...prev,
        [signatureModalField.id]: {
          imageBase64: sigData.imageBase64,
          timestamp: sigData.timestamp,
          role: signatureModalField.signatureRole || 'client',
        },
      }));
      setSignatureModalField(null);
    }
  };

  const handleClearSignature = (fieldId) => {
    setSignatures(prev => {
      const s = { ...prev };
      delete s[fieldId];
      return s;
    });
  };

  // Submit
  const handleSubmit = async () => {
    // Validate required client fields
    const allFields = (fieldDefinitions.pages || []).flatMap(p => p.fields || []);
    const requiredClientSigs = allFields.filter(
      f => f.type === 'signature' && f.required && !isAgentField(f)
    );
    for (const sig of requiredClientSigs) {
      if (!signatures[sig.id]?.imageBase64) {
        setError(`Please sign: ${sig.label || sig.name}`);
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const resp = await fetch(`${BASE_URL}/api/signing/form/${token}/submit/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: formData, signatures }),
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed.');
      }

      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Render field input
  const renderFieldInput = (field) => {
    const readOnly = isAgentField(field);

    if (field.type === 'signature') {
      const sig = signatures[field.id];

      // Agent signature already captured — show read-only
      if (readOnly) {
        if (formData[field.id]) {
          return (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
              Agent signed
            </div>
          );
        }
        return (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">
            Agent signature
          </div>
        );
      }

      if (sig?.imageBase64) {
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <img src={sig.imageBase64} alt="Signature" className="flex-1 object-contain" />
            <button
              onClick={() => handleClearSignature(field.id)}
              className="text-xs text-red-600 hover:underline"
            >
              Clear
            </button>
          </div>
        );
      }

      return (
        <button
          onClick={() => setSignatureModalField(field)}
          className="w-full h-full bg-yellow-50 border-2 border-dashed border-yellow-400 text-yellow-700 text-xs flex items-center justify-center hover:bg-yellow-100"
        >
          Click to Sign
        </button>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={!!formData[field.id]}
          onChange={e => handleFieldChange(field.id, e.target.checked)}
          disabled={readOnly}
          className="w-full h-full cursor-pointer"
        />
      );
    }

    if (field.type === 'date') {
      return (
        <input
          type="date"
          value={formData[field.id] || ''}
          onChange={e => handleFieldChange(field.id, e.target.value)}
          readOnly={readOnly}
          className={`w-full h-full border-0 bg-transparent text-xs px-1 ${readOnly ? 'text-gray-500' : ''}`}
          style={{ fontSize: field.fontSize || 10 }}
        />
      );
    }

    return (
      <input
        type="text"
        value={formData[field.id] || ''}
        onChange={e => handleFieldChange(field.id, e.target.value)}
        readOnly={readOnly}
        className={`w-full h-full border-0 bg-transparent px-1 ${readOnly ? 'text-gray-500 bg-gray-50' : ''}`}
        style={{ fontSize: field.fontSize || 10 }}
        placeholder={field.label}
      />
    );
  };

  // --- Render states ---

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-blue-800 p-4 flex justify-center">
          <img src={logo} alt="Mid Florida Agencies" style={{ height: 60 }} />
        </header>
        <div className="max-w-md mx-auto mt-16 bg-white rounded-lg shadow p-8 text-center">
          <div className="text-green-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">Form Signed Successfully</h2>
          <p className="text-gray-600 mb-4">
            Thank you, {clientName}! Your signed document has been submitted.
            Your agent will be notified.
          </p>
          <p className="text-sm text-gray-500">You may close this window.</p>
        </div>
      </div>
    );
  }

  if (error && !pdfUrl) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-blue-800 p-4 flex justify-center">
          <img src={logo} alt="Mid Florida Agencies" style={{ height: 60 }} />
        </header>
        <div className="max-w-md mx-auto mt-16 bg-white rounded-lg shadow p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2">
            {errorStatus === 'completed' ? 'Already Signed' : errorStatus === 'expired' ? 'Link Expired' : 'Unable to Load Form'}
          </h2>
          <p className="text-gray-600">{error}</p>
          {errorStatus === 'expired' && (
            <p className="text-sm text-gray-500 mt-4">Please contact your agent to request a new signing link.</p>
          )}
        </div>
      </div>
    );
  }

  // --- Main form view ---
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-blue-800 p-4 flex justify-center">
        <img src={logo} alt="Mid Florida Agencies" style={{ height: 60 }} />
      </header>

      {/* Title bar */}
      <div className="bg-white border-b p-3 shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">{templateName}</h1>
            <p className="text-sm text-gray-600">Please review and sign below, {clientName}</p>
          </div>
        </div>
      </div>

      {/* Error bar */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 p-3 text-red-700 text-sm text-center">
          {error}
          <button onClick={() => setError('')} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {/* PDF + fields */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-5xl mx-auto p-4">
          {/* Toolbar */}
          <div className="bg-white rounded-t-lg border p-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                >&lt;</button>
                <span className="text-sm">Page {currentPage} of {numPages || '?'}</span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(numPages || 1, p + 1))}
                  disabled={currentPage >= (numPages || 1)}
                  className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                >&gt;</button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))} className="px-2 py-1 bg-gray-200 rounded">-</button>
                <span className="text-sm">{Math.round(scale * 100)}%</span>
                <button onClick={() => setScale(s => Math.min(2, s + 0.1))} className="px-2 py-1 bg-gray-200 rounded">+</button>
              </div>
            </div>
          </div>

          {/* PDF display */}
          <div className="bg-gray-300 border-x p-4 overflow-auto">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="text-center p-8">Loading PDF...</div>}
              error={<div className="text-center text-red-600 p-8">Failed to load PDF.</div>}
            >
              <div
                ref={pageContainerRef}
                className="relative inline-block shadow-lg mx-auto"
                style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}
              >
                <Page pageNumber={currentPage} renderTextLayer={false} renderAnnotationLayer={false} />
                {getCurrentPageFields().map(field => (
                  <div
                    key={field.id}
                    className={`absolute ${
                      field.required && !formData[field.id] && field.type !== 'signature'
                        ? 'ring-2 ring-red-400'
                        : field.required && field.type === 'signature' && !signatures[field.id]
                        ? 'ring-2 ring-red-400'
                        : ''
                    }`}
                    style={{
                      left: field.x,
                      top: field.y,
                      width: field.width,
                      height: field.height,
                    }}
                  >
                    {renderFieldInput(field)}
                  </div>
                ))}
              </div>
            </Document>
          </div>

          {/* Footer / submit */}
          <div className="bg-white rounded-b-lg border p-4 flex justify-between items-center">
            <p className="text-sm text-gray-500">
              All fields marked with a red border are required.
            </p>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className={`px-6 py-3 rounded text-white font-medium ${
                submitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {submitting ? 'Submitting...' : 'Submit Signed Form'}
            </button>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {signatureModalField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {signatureModalField.label || 'Sign Here'}
            </h3>
            <SignatureCapture
              label={signatureModalField.label || 'Sign here'}
              signatureRole={signatureModalField.signatureRole || 'client'}
              captureMode="client"
              width={400}
              height={150}
              onCapture={handleSignatureCapture}
            />
            <div className="mt-4 text-right">
              <button
                onClick={() => setSignatureModalField(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
